import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { PIAPI_MODELS } from '@/types/activations';
import type { PhotoBoothVariant } from '@/types/activations';
import type { ModelCapabilityResponse } from '@/types/generated/activations.generated';

/**
 * Fetches the media-generation model CAPABILITY CATALOG (ADR-0036) that drives the PhotoBooth
 * variant Model dropdown and its capability-gated fields (negative prompt, aspect ratio).
 *
 * The backend now serves ONE vendor-correct catalog (PascalCase `Image`/`Video` media types,
 * PiAPI-correct `model`/`taskType`) from the Activations API:
 *   `GET admin/media-generation/model-catalog` → `ApiResponse<List<ModelCapabilityResponse>>`
 * (role-gated ATHLYN_ADMIN/ATHLYN_TEAM). It is consumed via the same `activationsApiRequest`
 * client the rest of the activations pages use, and typed by the generated
 * `ModelCapabilityResponse`. The endpoint is the source of truth; `PIAPI_MODELS` survives only
 * as an OUTAGE FALLBACK (see `PIAPI_MODELS_FALLBACK`).
 */
export const MEDIA_GENERATION_CATALOG_KEY = ['media-generation', 'model-catalog'] as const;
export const MEDIA_GENERATION_CATALOG_ENDPOINT = '/admin/media-generation/model-catalog';

/** Catalog is near-static — cache for an hour before considering it stale. */
const CATALOG_STALE_TIME = 1000 * 60 * 60;

/**
 * Fallback catalog built from the legacy hardcoded `PIAPI_MODELS` constant. Used ONLY when the
 * catalog request fails or returns nothing, so an endpoint outage can never brick the config form.
 * The conservative defaults reproduce today's EXACT behaviour:
 *   - `supportsNegativePrompt: false` — activations never sent a negative prompt before, so the
 *     new field stays hidden in fallback (no behaviour change).
 *   - `supportsAspectRatio: true`     — the aspect-ratio field was always available.
 * `defaultAspectRatio` is carried over from each option's `aspectRatio`.
 *
 * This is the ONLY place `PIAPI_MODELS` is imported (the form no longer reads it).
 */
export const PIAPI_MODELS_FALLBACK: ModelCapabilityResponse[] = PIAPI_MODELS.map((m) => ({
  mediaType: m.mediaType,
  provider: m.provider,
  model: m.model,
  taskType: m.taskType,
  supportsNegativePrompt: false,
  supportsAspectRatio: true,
  defaultAspectRatio: m.aspectRatio ?? null,
  parameters: [],
}));

/**
 * The generated `ModelCapabilityResponse` carries no display label, so the dropdown derives one:
 * known models reuse the friendly `PIAPI_MODELS` label, and anything else falls back to its
 * model/taskType slug. Purely cosmetic — selection/matching is keyed on provider/model/taskType.
 */
const LABEL_BY_TASK_TYPE = new Map(PIAPI_MODELS.map((m) => [m.taskType, m.label]));

export function catalogModelLabel(m: ModelCapabilityResponse): string {
  return (
    (m.taskType ? LABEL_BY_TASK_TYPE.get(m.taskType) : undefined) ||
    m.model ||
    m.taskType ||
    'Model'
  );
}

/**
 * Resolves the catalog entry that matches a variant's provider/model/taskType against a
 * SUPPLIED catalog array (never a global), or null when the variant uses a custom model not in
 * the catalog. The form uses this so it does not re-derive matching against a module-level list.
 */
export function matchModelCapability(
  catalog: ModelCapabilityResponse[],
  variant: Pick<PhotoBoothVariant, 'provider' | 'model' | 'taskType'>,
): ModelCapabilityResponse | null {
  return (
    catalog.find(
      (c) =>
        c.provider === variant.provider &&
        c.model === variant.model &&
        c.taskType === variant.taskType,
    ) ?? null
  );
}

export function useMediaGenerationCatalog(): UseQueryResult<ModelCapabilityResponse[]> {
  return useQuery<ModelCapabilityResponse[]>({
    queryKey: MEDIA_GENERATION_CATALOG_KEY,
    queryFn: () =>
      activationsApiRequest<ModelCapabilityResponse[]>('GET', MEDIA_GENERATION_CATALOG_ENDPOINT),
    staleTime: CATALOG_STALE_TIME,
  });
}

/**
 * Resolves the catalog the form should render. A failed OR empty (or non-array) catalog response
 * falls back to the built-in list rather than an empty dropdown; loading is surfaced separately so
 * the form can show a loading affordance without blocking editing.
 */
export function resolveCatalog(query: UseQueryResult<ModelCapabilityResponse[]>): {
  models: ModelCapabilityResponse[];
  isLoading: boolean;
  usingFallback: boolean;
} {
  const hasData = !query.isError && Array.isArray(query.data) && query.data.length > 0;
  return {
    models: hasData ? query.data! : PIAPI_MODELS_FALLBACK,
    isLoading: query.isLoading,
    usingFallback: !hasData,
  };
}
